// SPDX-License-Identifier: MIT

// Component CSS for the consent UI, themed entirely through the CSS custom
// properties emitted by config.js buildThemeCss (--primaryColor, --textColor,
// etc.). Injected once by ConsentManager via a <style> element, so it ships with
// the component and needs no separate stylesheet or CSS-module tooling.

const COMPONENT_CSS = `
#cc-root {
  position: fixed;
  inset: 0;
  z-index: 99999;
  pointer-events: none;
  font-family: var(--fontFamily);
}
#cc-root *, #cc-root *::before, #cc-root *::after { box-sizing: border-box; }

#cc-backdrop {
  position: absolute;
  inset: 0;
  background-color: var(--backdropBackgroundColor);
  backdrop-filter: blur(var(--backdropBackgroundBlur));
  pointer-events: auto;
}

.cc-panel {
  pointer-events: auto;
  position: fixed;
  background-color: var(--backgroundColor);
  color: var(--textColor);
  border-radius: 12px;
  box-shadow: var(--boxShadow);
  padding: 32px;
  width: 600px;
  max-width: calc(100% - 32px);
  max-height: calc(100vh - 32px);
  overflow: auto;
}
.cc-panel p { font-size: 16px; line-height: 24px; margin: 0 0 15px; color: var(--textColor); }
.cc-panel a { color: var(--primaryColor); text-decoration: underline; }

/* Banner positions */
.cc-pos-bottomRight { bottom: 16px; right: 16px; }
.cc-pos-bottomLeft { bottom: 16px; left: 16px; }
.cc-pos-bottomCenter { bottom: 16px; left: 50%; transform: translateX(-50%); }
.cc-pos-center { top: 50%; left: 50%; transform: translate(-50%, -50%); }

/* Buttons */
.cc-button {
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  padding: 10px 20px;
  border-radius: 5px;
  border: 2px solid var(--primaryColor);
  background-color: var(--primaryColor);
  color: var(--backgroundColor);
  cursor: pointer;
}
.cc-button:hover { background-color: var(--backgroundColor); color: var(--primaryColor); }
.cc-button-link {
  border: none;
  background: transparent;
  color: var(--primaryColor);
  cursor: pointer;
  font: inherit;
  padding: 15px 0;
  text-decoration: none;
}
.cc-button-link:hover { color: var(--textColor); }

.cc-actions { display: flex; flex-direction: column; gap: 16px; margin-top: 24px; }
@media (min-width: 600px) { .cc-actions { flex-direction: row; align-items: center; } }

/* Focus */
#cc-root a:focus-visible,
#cc-root button:focus-visible,
#cc-root .cc-toggle:focus-within {
  outline: none;
  box-shadow: 0 0 0 2px var(--backgroundColor), 0 0 0 4px var(--textColor);
  border-radius: 5px;
}

/* Modal */
#cc-modal { width: 800px; padding: 30px; display: flex; flex-direction: column; }
#cc-modal header { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 20px; }
#cc-modal h2 { font-size: 24px; font-weight: 500; margin: 0; color: var(--textColor); }
#cc-modal .cc-close { border: none; background: var(--backgroundColor); color: var(--primaryColor); cursor: pointer; padding: 8px; display: inline-flex; }
#cc-modal .cc-close svg { fill: var(--primaryColor); }
#cc-modal .cc-types { margin-top: 24px; }
#cc-modal fieldset { border: none; padding: 0; margin: 0 0 32px; }
#cc-modal fieldset:last-of-type { margin-bottom: 0; }
#cc-modal legend { font-weight: 700; font-size: 16px; margin-bottom: 10px; color: var(--textColor); padding: 0; }
#cc-modal .cc-type-row { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
#cc-modal footer { display: flex; flex-direction: column; gap: 16px; margin-top: 24px; }
@media (min-width: 600px) { #cc-modal footer { flex-direction: row; align-items: center; } }

/* Toggle */
.cc-toggle { position: relative; display: inline-block; width: 74px; height: 34px; flex-shrink: 0; cursor: pointer; }
.cc-toggle input { position: absolute; inset: 0; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
.cc-toggle input:disabled { cursor: not-allowed; }
.cc-track { position: absolute; inset: 0; background: var(--textColor); border-radius: 25px; transition: background 150ms ease-out; }
.cc-thumb { position: absolute; top: 2px; left: 2px; width: 30px; height: 30px; border-radius: 50%; background: var(--backgroundColor); transition: left 150ms ease-out; }
/* ON / OFF labels: each fills the half of the track beside the thumb and
   centers its text (both axes). Only one is shown at a time (opacity). */
.cc-toggle-off, .cc-toggle-on {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--backgroundColor);
  pointer-events: none;
  transition: opacity 150ms ease-out;
}
.cc-toggle-off { right: 0; opacity: 1; }
.cc-toggle-on { left: 0; opacity: 0; }
.cc-toggle input:checked ~ .cc-track { background: var(--primaryColor); }
.cc-toggle input:checked ~ .cc-thumb { left: calc(100% - 32px); }
.cc-toggle input:checked ~ .cc-toggle-off { opacity: 0; }
.cc-toggle input:checked ~ .cc-toggle-on { opacity: 1; }
.cc-toggle input:disabled ~ .cc-track { opacity: 0.6; filter: grayscale(100%); }

/* Cookie icon */
#cc-icon {
  pointer-events: auto;
  position: fixed;
  bottom: 10px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  padding: 0;
  background-color: var(--iconColor);
  box-shadow: 0 0 6px 0 #0000001a;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}
#cc-icon.cc-pos-bottomLeft { left: 10px; }
#cc-icon.cc-pos-bottomRight { right: 10px; }
#cc-icon svg { fill: var(--iconBackgroundColor); }

@media (prefers-reduced-motion: reduce) {
  .cc-track, .cc-thumb { transition: none; }
}
`;

module.exports = { COMPONENT_CSS };
